import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle, Archive, ArrowRight, Building2, CheckCircle2, FileText,
  Lock, Plus, RotateCcw, Search, Wrench,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { useQuery } from "@tanstack/react-query";
import { useContractCounts, useContractList } from "@/hooks/useContracts";
import { fetchAvailableLegalEntities } from "@/api/contracts";
import { CreateContractDrawer } from "@/components/contract/CreateContractDrawer";
import type {
  ContractListItem, ContractState, ContractView, ContractWorkspaceActions,
} from "@/api/types";

// WS-1 (D-101/D-102, мокап v2) — Договори · Workspace.
// ВСІ числа — з WS-0 counts snapshot (KPI == tab == list == SQL, backend-owned);
// usage-вісь = навігація (таби), workflow = бейдж у рядку; terms/lifecycle —
// ВИКЛЮЧНО resolver-payload; write-CTA — ВИКЛЮЧНО backend allowedActions
// (AC-WS-CTA-GATED: 0 frontend-інференсу прав, 0 мертвих CTA на RO-поверхні).

const PAGE = 50;

type Sel =
  | "all" | "active_in_work" | "needs_action" | "pending_approval"
  | "source_1c" | "source_crm" | "archive";

const TABS: Array<{ sel: Sel; label: string }> = [
  { sel: "active_in_work", label: "Активні в роботі" },
  { sel: "needs_action", label: "Потребують дії" },
  { sel: "pending_approval", label: "На погодженні" },
  { sel: "source_1c", label: "З 1С" },
  { sel: "archive", label: "Архів" },
];

// AC-WS-TOOLTIP-A11Y — copy LOCKED (BuildNotes v1).
const ACTIVE_TOOLTIP =
  "Юридично активні договори, за якими є реальне використання / відгрузки. " +
  "Юридичний стан кожного договору показано окремим бейджем у рядку.";

const TYPE_LABEL: Record<string, string> = {
  supply_agreement: "Постачання", dealer_agreement: "Дилерський",
  marketing_agreement: "Маркетинговий", nda: "NDA", other: "Інший",
};

const WF_BADGE: Partial<Record<ContractState, { label: string; cls: string }>> = {
  draft: { label: "чернетка", cls: "border bg-muted text-muted-foreground" },
  rejected: { label: "відхилено", cls: "bg-destructive-soft text-destructive-soft-foreground" },
  pending_approval_sales: { label: "погодження · продажі", cls: "bg-info text-info-foreground" },
  pending_approval_legal: { label: "погодження · юрист", cls: "bg-info text-info-foreground" },
  approved: { label: "погоджено", cls: "bg-info text-info-foreground" },
  sent_to_client: { label: "надіслано клієнту", cls: "bg-info text-info-foreground" },
  signed: { label: "підписаний", cls: "bg-primary/10 text-primary" },
  active: { label: "діючий", cls: "bg-primary/10 text-primary" },
  expired: { label: "прострочений", cls: "border bg-muted text-muted-foreground" },
  terminated: { label: "розірваний", cls: "border bg-muted text-muted-foreground" },
  archived: { label: "архів (юр.)", cls: "border bg-muted text-muted-foreground" },
  inactive: { label: "неактивний", cls: "border bg-muted text-muted-foreground" },
};

const USAGE_BADGE: Record<string, { label: string; cls: string }> = {
  active_recent: { label: "у використанні", cls: "bg-primary/10 text-primary" },
  archive_candidate: { label: "кандидат в архів", cls: "bg-warning text-warning-foreground" },
  archived: { label: "архівовано", cls: "border bg-muted text-muted-foreground" },
};

const STATUS_GROUPS: Record<string, { label: string; states: ContractState[] }> = {
  active: { label: "Діючі", states: ["active", "signed"] },
  approval: { label: "На погодженні", states: ["pending_approval_sales", "pending_approval_legal", "approved", "sent_to_client"] },
  draft: { label: "Чернетки", states: ["draft", "rejected"] },
  done: { label: "Завершені", states: ["expired", "terminated", "archived"] },
  inactive: { label: "Неактивні", states: ["inactive"] },
};

const nf = new Intl.NumberFormat("uk-UA");

/** «остання відгрузка»: N дн тому / дата для давніх. */
function shipLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86_400_000));
  if (days === 0) return "сьогодні";
  if (days <= 90) return `${days} дн тому`;
  return d.toLocaleDateString("uk-UA");
}

function Badge({ cls, children, title }: { cls: string; children: React.ReactNode; title?: string }) {
  return (
    <span title={title} className={cn("inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold", cls)}>
      {children}
    </span>
  );
}

// ── KPI row ──────────────────────────────────────────────────────────────────

function KpiCard({ n, label, active, accent, zero, onClick, tooltip, tooltipId }: {
  n: number; label: string; active: boolean; accent?: "attn" | "appr" | "arch";
  zero?: boolean; onClick: () => void; tooltip?: string; tooltipId?: string;
}) {
  // AC-WS-KPI-ZERO: count=0 лишається навігацією, але нейтральний zero-стиль.
  const numCls = zero
    ? "text-muted-foreground/60 font-semibold"
    : accent === "attn" ? "text-warning-foreground font-bold"
    : accent === "appr" ? "text-info-foreground font-bold"
    : accent === "arch" ? "text-muted-foreground font-bold"
    : "font-bold";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-describedby={tooltip ? tooltipId : undefined}
      className={cn(
        "group relative min-w-[124px] flex-1 rounded-lg border bg-card px-3.5 py-2.5 text-left",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active ? "border-primary ring-1 ring-inset ring-primary" : "hover:border-primary/40",
      )}
    >
      <div className={cn("text-[21px] leading-tight tracking-tight", numCls)}>{nf.format(n)}</div>
      <div className={cn("mt-0.5 flex items-center gap-1 text-[11.5px] font-medium", zero ? "text-muted-foreground/60" : "text-muted-foreground")}>
        {label}
        {tooltip && (
          <span
            aria-hidden
            className="inline-flex h-[14px] w-[14px] cursor-help items-center justify-center rounded-full border border-muted-foreground/50 text-[9px] text-muted-foreground"
          >
            ?
          </span>
        )}
        {tooltip && (
          /* AC-WS-TOOLTIP-A11Y: keyboard-accessible — показується на focus-visible
             КАРТКИ (focus-trigger) + aria-describedby на самій кнопці; copy LOCKED. */
          <span
            id={tooltipId}
            role="tooltip"
            className="invisible absolute left-0 top-full z-20 mt-1.5 w-72 whitespace-normal rounded-md bg-foreground px-3 py-2 text-left text-[11.5px] font-normal leading-relaxed text-background opacity-0 transition-opacity group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100"
          >
            {tooltip}
          </span>
        )}
      </div>
    </button>
  );
}

// ── Row ──────────────────────────────────────────────────────────────────────

function WorkspaceRow({ c, actions, onOpen, onClient, onResolve }: {
  c: ContractListItem;
  actions: ContractWorkspaceActions;
  onOpen: () => void;
  onClient: (() => void) | null;
  onResolve: () => void;
}) {
  const lc = c.lifecycle;
  const type = TYPE_LABEL[c.contractType] ?? c.contractType;
  const wf = WF_BADGE[c.currentState] ?? { label: c.currentState, cls: "border bg-muted text-muted-foreground" };
  const usage = lc?.usageStatus ? USAGE_BADGE[lc.usageStatus] : null;
  const isCrm = c.source !== "sync_1c";
  const isDraftToContinue = isCrm && c.currentState === "draft";
  const anomaly = lc?.leAnomaly === true;
  const client = c.holding?.name || c.account?.name || "—";
  const accountLine = c.account?.name && c.account.name !== client ? c.account.name : null;

  // D-102: «Розібрати» лише за capability; інакше read-only «Відкрити».
  const primary = anomaly && actions.canResolveAnomaly
    ? { label: "Розібрати", onClick: onResolve, icon: <Wrench className="h-3.5 w-3.5" /> }
    : isDraftToContinue && actions.canContinueDraft
      ? { label: "Продовжити", onClick: onOpen, icon: <ArrowRight className="h-3.5 w-3.5" /> }
      : { label: "Відкрити", onClick: onOpen, icon: null as React.ReactNode };

  return (
    <div
      className={cn(
        "grid grid-cols-1 items-center gap-x-3 gap-y-1.5 border-b border-border/60 px-4 py-3 hover:bg-accent/40",
        "md:grid-cols-[minmax(200px,1.3fr)_minmax(170px,1.1fr)_minmax(140px,1fr)_minmax(150px,1fr)_auto]",
        anomaly && "bg-warning/40",
      )}
    >
      <div className="min-w-0">
        <button type="button" onClick={onOpen} className="block max-w-full truncate text-left text-[13.5px] font-semibold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {type}{isCrm ? " (CRM)" : ""} {c.number ? `· № ${c.number}` : c.name ? `· ${c.name}` : ""}
        </button>
        <div className="mt-1 flex flex-wrap items-center gap-1">
          {c.source === "sync_1c"
            ? <Badge cls="bg-info text-info-foreground">1С</Badge>
            : <Badge cls="bg-primary/10 text-primary">CRM</Badge>}
          <Badge cls={wf.cls}>{wf.label}</Badge>
          {usage && <Badge cls={usage.cls}>{usage.label}</Badge>}
          {/* D-140 W5/PATCH-10 — client-archived, DISTINCT from the contract's own archive badge above */}
          {c.clientArchived && <Badge cls="border bg-muted text-muted-foreground"><Archive className="h-3 w-3" />Клієнт архівний</Badge>}
          {anomaly && (
            <Badge cls="bg-destructive-soft text-destructive-soft-foreground">
              <AlertTriangle className="h-3 w-3" />LE неактивна, є відгрузки
            </Badge>
          )}
        </div>
      </div>

      <div className="min-w-0">
        <div className="flex items-center gap-1.5 truncate text-[13px] font-medium">
          <Building2 className="h-3 w-3 shrink-0 text-muted-foreground" />
          <span className="truncate">{client}</span>
        </div>
        {accountLine && <div className="mt-0.5 truncate text-[11.5px] text-muted-foreground">{accountLine}</div>}
      </div>

      <div className="text-[11.5px] text-muted-foreground">
        {c.hasTerms ? (
          c.deferralDays !== null && c.deferralDays !== undefined
            ? <span><b className="font-semibold text-foreground">Відстрочка {c.deferralDays} дн</b></span>
            : <span className="font-medium text-foreground">умови задані</span>
        ) : (
          <span className="rounded bg-warning px-1.5 py-0.5 font-medium text-warning-foreground">умови не задані</span>
        )}
        {c.lastShipmentDate && (
          <div className="mt-0.5 text-[11px] text-muted-foreground/70">остання відгрузка: {shipLabel(c.lastShipmentDate)}</div>
        )}
      </div>

      <div>
        {lc?.missingKormaxLe ? (
          <Badge cls="border border-dashed border-warning-foreground/60 bg-warning text-warning-foreground">без юрособи Kormax</Badge>
        ) : c.legalEntity ? (
          <Badge cls="border bg-muted text-muted-foreground">Kormax-LE: {c.legalEntity.name}</Badge>
        ) : null}
      </div>

      <div className="flex shrink-0 gap-1.5">
        <button
          type="button"
          onClick={primary.onClick}
          className="inline-flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {primary.icon}{primary.label}
        </button>
        {onClient && (
          <button
            type="button"
            onClick={onClient}
            className="rounded-lg border px-2.5 py-1.5 text-[11.5px] font-semibold hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            До клієнта
          </button>
        )}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function ContractsWorkspace() {
  const navigate = useNavigate();
  const [sel, setSel] = useState<Sel>("active_in_work");
  const [archiveChip, setArchiveChip] = useState<"candidates" | "archived">("candidates");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  // WS-1.1 — server-side toolbar filters (resolver predicates; intersect the view).
  const [fSource, setFSource] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fLe, setFLe] = useState("all");
  const [fUsage, setFUsage] = useState("all");
  const [fArchived, setFArchived] = useState<"exclude" | "include" | "only">("exclude"); // D-140 W6

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const view: ContractView =
    sel === "archive" ? (archiveChip === "candidates" ? "archive_candidates" : "archived") : sel;

  const counts = useContractCounts();
  const list = useContractList({
    view,
    search: search || undefined,
    limit: PAGE,
    offset: page * PAGE,
    source: fSource !== "all" ? fSource : undefined,
    statusGroup: fStatus !== "all" ? fStatus : undefined,
    kormaxLe: fLe !== "all" ? fLe : undefined,
    usageStatus: fUsage !== "all" ? (fUsage === "none" ? "none" : fUsage) : undefined,
    archived: fArchived, // D-140 W6 — archived-CLIENT tri-state (≠ contract usage archive)
  });
  // Kormax-LE filter options — canonical reference (12 LEs), not page-derived.
  const leQuery = useQuery({
    queryKey: ["contracts", "le-options"],
    queryFn: ({ signal }) => fetchAvailableLegalEntities(signal),
    staleTime: 10 * 60_000,
  });

  const c = counts.data;
  const actions: ContractWorkspaceActions =
    list.data?.allowedActions ?? c?.allowedActions ?? { canCreateContract: false, canResolveAnomaly: false, canContinueDraft: false };
  const readOnly = !actions.canCreateContract && !actions.canResolveAnomaly && !actions.canContinueDraft;

  const filtersActive = fSource !== "all" || fStatus !== "all" || fLe !== "all" || fUsage !== "all" || fArchived !== "exclude";

  const listData = list.data;
  const rawItems = useMemo(() => listData?.items ?? [], [listData]);

  const leOptions = useMemo(
    () => (leQuery.data?.list ?? []).map((le) => [le.id, le.name] as const),
    [leQuery.data],
  );

  // Filters are server-side (WS-1.1); client only applies the default sort:
  // needs-action → approval → archive-cand → active (мокап).
  const items = useMemo(() => {
    const bucket = (r: ContractListItem) => {
      const lc = r.lifecycle;
      if (lc && lc.needsActionReasons.length > 0) return 0;
      if (r.currentState === "pending_approval_sales" || r.currentState === "pending_approval_legal") return 1;
      if (lc?.isArchiveCandidate) return 2;
      return 3;
    };
    return [...rawItems].sort((a, b) => bucket(a) - bucket(b));
  }, [rawItems]);

  const scopeTotal = list.data?.scopeTotal ?? 0;
  const from = scopeTotal === 0 ? 0 : page * PAGE + 1;
  const to = page * PAGE + rawItems.length;
  const lastPage = Math.max(0, Math.ceil(scopeTotal / PAGE) - 1);

  const resetAll = () => {
    setSearchInput(""); setSearch(""); setFSource("all"); setFStatus("all"); setFLe("all"); setFUsage("all"); setFArchived("exclude"); setPage(0);
  };

  const selectKpi = (s: Sel, chip?: "candidates" | "archived") => {
    setSel(s);
    if (chip) setArchiveChip(chip);
    setPage(0);
  };

  const tabCount = (s: Sel): number | null => {
    if (!c) return null;
    switch (s) {
      case "active_in_work": return c.kpi.activeInWork;
      case "needs_action": return c.kpi.needsAction;
      case "pending_approval": return c.kpi.pendingApproval;
      case "source_1c": return c.bySource.sync_1c;
      case "archive": return c.archive.candidates + c.archive.archived; // AC-WS-ARCHIVE-COUNT: точне число
      default: return c.all;
    }
  };

  const showPendingEmpty = sel === "pending_approval" && !list.isPending && !list.isError && scopeTotal === 0 && !search && !filtersActive;
  const showNoResults = !list.isPending && !list.isError && !showPendingEmpty
    && rawItems.length === 0 && (search !== "" || filtersActive);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      {/* header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">Договори · Workspace</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            usage-вісь = навігація · юридичний стан — бейдж у рядку · числа backend-owned
          </p>
        </div>
        <div className="flex items-center gap-2">
          {actions.canResolveAnomaly && (
            <button
              type="button"
              onClick={() => navigate("/contracts/archive-review")}
              className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Archive className="h-4 w-4" />Архів-рев'ю
            </button>
          )}
          {actions.canCreateContract && (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Plus className="h-4 w-4" />Створити договір
            </button>
          )}
        </div>
      </div>

      {readOnly && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-warning-foreground/20 bg-warning px-3 py-2 text-sm font-medium text-warning-foreground">
          <Lock className="h-4 w-4" />Режим перегляду. Дії з договорами недоступні.
        </div>
      )}

      {/* KPI row — ВСІ числа з counts snapshot; клік == таб/фільтр */}
      {counts.isError ? (
        <div className="mb-3.5 rounded-lg border bg-card p-4 text-center text-sm text-muted-foreground">
          Не вдалося завантажити показники.{" "}
          <button type="button" onClick={() => counts.refetch()} className="font-medium text-primary hover:underline">Повторити</button>
        </div>
      ) : (
        <div className="mb-3.5 flex flex-wrap items-stretch gap-2.5">
          <KpiCard n={c?.all ?? 0} label="Всі" active={sel === "all"} onClick={() => selectKpi("all")} />
          <KpiCard
            n={c?.kpi.activeInWork ?? 0} label="Активні в роботі" active={sel === "active_in_work"}
            onClick={() => selectKpi("active_in_work")} tooltip={ACTIVE_TOOLTIP} tooltipId="tip-active-in-work"
          />
          <KpiCard n={c?.kpi.needsAction ?? 0} label="Потребують дії" accent="attn" zero={c?.kpi.needsAction === 0} active={sel === "needs_action"} onClick={() => selectKpi("needs_action")} />
          <KpiCard n={c?.kpi.pendingApproval ?? 0} label="На погодженні" accent="appr" zero={c?.kpi.pendingApproval === 0} active={sel === "pending_approval"} onClick={() => selectKpi("pending_approval")} />
          <KpiCard n={c?.kpi.archiveCandidates ?? 0} label="Архів-кандидати" accent="arch" active={sel === "archive" && archiveChip === "candidates"} onClick={() => selectKpi("archive", "candidates")} />
          <div className="mx-0.5 my-1.5 w-px bg-border" aria-hidden />
          {/* AC-WS-SOURCE-NOTE: source-фільтри складу, НЕ action-KPI */}
          <button type="button" onClick={() => selectKpi("source_crm")} aria-pressed={sel === "source_crm"}
            className={cn("min-w-[104px] rounded-lg border border-dashed bg-muted/40 px-3.5 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", sel === "source_crm" && "border-solid border-primary ring-1 ring-inset ring-primary")}>
            <div className="text-[15px] font-semibold">{nf.format(c?.bySource.crm_managed ?? 0)}</div>
            <div className="mt-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground/70">CRM-створені</div>
          </button>
          <button type="button" onClick={() => selectKpi("source_1c")} aria-pressed={sel === "source_1c"}
            className={cn("min-w-[104px] rounded-lg border border-dashed bg-muted/40 px-3.5 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", sel === "source_1c" && "border-solid border-primary ring-1 ring-inset ring-primary")}>
            <div className="text-[15px] font-semibold">{nf.format(c?.bySource.sync_1c ?? 0)}</div>
            <div className="mt-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground/70">З 1С</div>
          </button>
          <div className="max-w-[76px] self-center text-[10px] leading-snug text-muted-foreground/70">source-фільтри, не «дії»</div>
        </div>
      )}

      {/* workspace panel */}
      <div className="rounded-xl border bg-card shadow-sm">
        {/* tabs */}
        <div className="flex flex-wrap gap-0.5 border-b px-3" role="tablist">
          {TABS.map((t) => {
            const n = tabCount(t.sel);
            const on = sel === t.sel;
            return (
              <button
                key={t.sel}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => { setSel(t.sel); setPage(0); }}
                className={cn(
                  "flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-[13px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  on ? "border-primary font-semibold text-primary" : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {t.label}
                {/* лічильник 0 → бейдж прихований (мокап) */}
                {n !== null && n > 0 && (
                  <span className={cn("rounded-full border px-1.5 text-[10.5px] font-bold", on ? "border-primary/30 bg-primary/10 text-primary" : "bg-muted text-muted-foreground")}>
                    {nf.format(n)}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* archive chips — candidate ≠ archived (AC-WS-ARCHIVE-001) */}
        {sel === "archive" && (
          <div className="flex gap-1.5 border-b border-border/60 px-4 py-2.5">
            {([["candidates", "Кандидати", c?.archive.candidates], ["archived", "Архівовано", c?.archive.archived]] as const).map(([key, label, n]) => (
              <button
                key={key}
                type="button"
                aria-pressed={archiveChip === key}
                onClick={() => { setArchiveChip(key); setPage(0); }}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  archiveChip === key ? "border-warning-foreground/50 bg-warning text-warning-foreground" : "text-muted-foreground hover:bg-accent",
                )}
              >
                {label} · {n !== undefined ? nf.format(n) : "…"}
              </button>
            ))}
          </div>
        )}

        {/* toolbar: search (server) + filters (client refinement) */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border/60 px-4 py-2.5">
          <div className="flex min-w-[230px] flex-1 items-center gap-2 rounded-lg border px-2.5 py-1.5">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Пошук договорів"
              placeholder="Пошук: № договору · назва · клієнт · ЄДРПОУ"
              className="w-full bg-transparent text-[12.5px] outline-none placeholder:text-muted-foreground/70"
            />
          </div>
          <select value={fSource} onChange={(e) => { setFSource(e.target.value); setPage(0); }} aria-label="Джерело"
            className={cn("rounded-lg border bg-card px-2 py-1.5 text-xs font-medium", fSource !== "all" && "border-primary/40 bg-primary/5 text-primary")}>
            <option value="all">Джерело · всі</option>
            <option value="sync_1c">З 1С</option>
            <option value="crm_managed">CRM-створені</option>
          </select>
          <select value={fStatus} onChange={(e) => { setFStatus(e.target.value); setPage(0); }} aria-label="Статус-група"
            className={cn("rounded-lg border bg-card px-2 py-1.5 text-xs font-medium", fStatus !== "all" && "border-primary/40 bg-primary/5 text-primary")}>
            <option value="all">Статус-група · всі</option>
            {Object.entries(STATUS_GROUPS).map(([k, g]) => <option key={k} value={k}>{g.label}</option>)}
          </select>
          <select value={fLe} onChange={(e) => { setFLe(e.target.value); setPage(0); }} aria-label="Юрособа Kormax"
            className={cn("rounded-lg border bg-card px-2 py-1.5 text-xs font-medium", fLe !== "all" && "border-primary/40 bg-primary/5 text-primary")}>
            <option value="all">Юрособа Kormax · всі</option>
            <option value="missing">Без юрособи Kormax</option>
            {leOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <select value={fUsage} onChange={(e) => { setFUsage(e.target.value); setPage(0); }} aria-label="Стан використання"
            className={cn("rounded-lg border bg-card px-2 py-1.5 text-xs font-medium", fUsage !== "all" && "border-primary/40 bg-primary/5 text-primary")}>
            <option value="all">Стан використання · всі</option>
            <option value="active_recent">У використанні</option>
            <option value="archive_candidate">Кандидат в архів</option>
            <option value="archived">Архівовано</option>
            <option value="none">Без класифікації (CRM)</option>
          </select>

          {/* D-140 W6 — archived CLIENT filter (≠ «Стан використання» = архів договору) */}
          <select value={fArchived} onChange={(e) => { setFArchived(e.target.value as "exclude" | "include" | "only"); setPage(0); }} aria-label="Архівні клієнти"
            className={cn("rounded-lg border bg-card px-2 py-1.5 text-xs font-medium", fArchived !== "exclude" && "border-primary/40 bg-primary/5 text-primary")}>
            <option value="exclude">Клієнт · активні</option>
            <option value="include">Клієнт · усі</option>
            <option value="only">Клієнт · тільки архівні</option>
          </select>
        </div>

        {/* body */}
        {list.isPending ? (
          <div className="space-y-2 p-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />)}</div>
        ) : list.isError ? (
          <div className="p-8 text-center">
            <div className="text-sm font-medium">Не вдалося завантажити договори</div>
            <button type="button" onClick={() => list.refetch()} className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
              <RotateCcw className="h-4 w-4" />Повторити
            </button>
          </div>
        ) : showPendingEmpty ? (
          <div className="m-4 rounded-lg border border-dashed p-8 text-center">
            <CheckCircle2 className="mx-auto h-6 w-6 text-primary" />
            <div className="mt-2 text-[13.5px] font-semibold">Немає договорів на погодженні</div>
            <div className="mt-1 text-xs text-muted-foreground">
              CRM-договори потрапляють сюди після подання на погодження (продажі → юрист)
            </div>
            {/* AC-WS-CTA-GATED: RO-empty — пояснення БЕЗ create-CTA */}
            {actions.canCreateContract && (
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-[12.5px] font-semibold text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="h-4 w-4" />Створити договір
              </button>
            )}
          </div>
        ) : showNoResults ? (
          <div className="m-4 rounded-lg border border-dashed p-8 text-center">
            <Search className="mx-auto h-6 w-6 text-muted-foreground" />
            <div className="mt-2 text-[13.5px] font-semibold">Нічого не знайдено</div>
            <div className="mt-1 text-xs text-muted-foreground">
              {search ? <>За запитом «{search}»</> : <>За поточними фільтрами</>}
              {filtersActive && search ? " з поточними фільтрами" : ""} немає договорів
            </div>
            <button
              type="button"
              onClick={resetAll}
              className="mt-3 rounded-lg border border-primary/30 px-3.5 py-2 text-[12.5px] font-semibold text-primary hover:bg-primary/5"
            >
              Очистити пошук і фільтри
            </button>
          </div>
        ) : items.length === 0 ? (
          /* FS-4: generic empty tab → proper empty-state (mirror showPendingEmpty),
             not a bare dead-end. CTA gated by backend allowedActions (no dead CTA). */
          <div className="m-4 rounded-lg border border-dashed p-8 text-center">
            <FileText className="mx-auto h-6 w-6 text-muted-foreground" />
            <div className="mt-2 text-[13.5px] font-semibold">У цьому розділі договорів немає</div>
            <div className="mt-1 text-xs text-muted-foreground">
              Договори з 1С — у вкладці «З 1С». CRM-договори створюються вручну.
            </div>
            {actions.canCreateContract && (
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-[12.5px] font-semibold text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="h-4 w-4" />Створити договір
              </button>
            )}
          </div>
        ) : (
          <div>
            {items.map((row) => (
              <WorkspaceRow
                key={row.id}
                c={row}
                actions={actions}
                onOpen={() => navigate(`/contracts/${row.id}`)}
                onClient={row.holding ? () => navigate(`/clients/${row.holding!.id}`) : null}
                onResolve={() => navigate("/contracts/archive-review")}
              />
            ))}
          </div>
        )}

        {/* pager — server-side numbers */}
        {!list.isPending && !list.isError && scopeTotal > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-xs text-muted-foreground">
            <div>
              {from}–{to} з {nf.format(scopeTotal)} · server-side
              {filtersActive && <span> · фільтри застосовано (server)</span>}
            </div>
            <div className="flex items-center gap-1.5">
              <button type="button" aria-label="Попередня сторінка" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="rounded-md border px-2.5 py-1 font-medium disabled:opacity-40">←</button>
              <span className="rounded-md border border-primary/30 bg-primary/10 px-2.5 py-1 font-semibold text-primary">{page + 1}</span>
              <span>з {lastPage + 1}</span>
              <button type="button" aria-label="Наступна сторінка" disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}
                className="rounded-md border px-2.5 py-1 font-medium disabled:opacity-40">→</button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground/70">
        <FileText className="h-3 w-3" />
        Картка договору не змінилась — «Відкрити» веде на існуючу. Detail-картка та архів-рев'ю — ті самі.
      </div>

      {createOpen && <CreateContractDrawer open={createOpen} onClose={() => setCreateOpen(false)} entryPoint="list" />}
    </div>
  );
}
